/* eslint-disable  @typescript-eslint/no-explicit-any */ // AJB: remove
import PillTextField from "@/app/shared/PillTextField";
import { Box, Divider, InputAdornment, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AspectMultiMenu, { LANDSCAPE_MAX_WIDTH } from "./AspectMultiMenu";
import {
  FlatAspect,
  HomePageSearchResponse,
} from "@/models/HomePageSearchResponse";
import HomePageSearchRequest from "@/models/HomePageSearchRequest";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AspectSelect from "./AspectSelect";
import DISTANCE_OPTIONS from "@/constants/DistanceOptions";
import PillButton from "@/app/shared/PillButton";
import AspectClearableLabel from "./AspectClearableLabel";
import { useBodyBackdrop } from "./useBodyBackdrop";
import { NAV_SELECTED_BACKGROUND_COLOR } from "@/app/layout/LayoutConstants";
import { useGooglePlaces } from "@/app/layout/useGooglePlaces";

function PlacePrediction({
  onClick,
  children,
}: {
  onClick: () => void;
  children?: React.ReactNode;
}) {
  return (
    <Typography
      sx={{
        cursor: "pointer",
        "&:hover": {
          textDecoration: "underline",
        },
      }}
      onClick={onClick}
    >
      {children}
    </Typography>
  );
}

export default function AspectLocationMenu({
  searchResponse,
  searchRequest,
  handleOnSearchRequestChange,
  isExpanded,
}: {
  searchResponse: HomePageSearchResponse;
  searchRequest: HomePageSearchRequest;
  handleOnSearchRequestChange: (request: any, skipRefresh: boolean) => void;
  isExpanded: boolean;
}) {
  const { setBackdropOpen } = useBodyBackdrop();
  const { setInput, options, getPlaceDetails } = useGooglePlaces({
    countryCode: "AU",
  });

  const handleSelectLocation = (
    locations: FlatAspect[] = [],
    label: string = "",
    lat: number = 0,
    lon: number = 0
  ) => {
    handleOnSearchRequestChange(
      {
        locations,
        area: {
          ...searchRequest.area,
          label,
          lat,
          lon,
        },
      },
      false
    );
    setBackdropOpen(false);
  };

  const isAreaSearchStarted = options.length > 0;
  const isAreaSearchSelected = !!searchRequest.area.label;

  return (
    <AspectMultiMenu
      label="Location"
      options={searchResponse.locations}
      selectedValues={
        isAreaSearchSelected
          ? [
              {
                id: searchRequest.area.label,
                label: `< ${searchRequest.area.distance} km from ${searchRequest.area.label}`,
              },
            ]
          : searchRequest.locations
      }
      onChange={(locations) => handleSelectLocation(locations)}
      icon={<PlaceOutlinedIcon />}
      isFilterBarExpanded={isExpanded}
      hideOptions={isAreaSearchStarted || isAreaSearchSelected}
      topSlot={
        <Box
          sx={(theme) => ({
            padding: 4,
            paddingBottom: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            [theme.breakpoints.down(LANDSCAPE_MAX_WIDTH)]: {
              flexDirection: "column",
              padding: 2,
              paddingBottom: 0,
              gap: 1,
            },
          })}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography variant="h6">Within</Typography>
            <AspectSelect
              aspectKey="distance"
              aspectLabel=""
              value={searchRequest.area.distance}
              options={DISTANCE_OPTIONS}
              onValueChange={(distance) => {
                handleOnSearchRequestChange(
                  {
                    area: {
                      ...searchRequest.area,
                      distance,
                    },
                  },
                  false
                );
                if (isAreaSearchSelected) {
                  setBackdropOpen(false);
                }
              }}
              fullWidth={false}
              size="small"
              minWidth={100}
            />
            <Typography variant="h6">of:</Typography>
          </Box>
          {isAreaSearchSelected ? (
            <PillButton startIcon={<PlaceOutlinedIcon />} isSelected={true}>
              <AspectClearableLabel
                label={searchRequest.area.label}
                isSelected={true}
                onClear={() => handleSelectLocation()}
              />
            </PillButton>
          ) : (
            <>
              <PillTextField
                placeholder="Suburb, city or region"
                sx={{
                  backgroundColor: NAV_SELECTED_BACKGROUND_COLOR,
                }}
                onChange={(evt) => {
                  setInput(evt.target.value);
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {options.map((prediction) => (
                  <PlacePrediction
                    key={prediction.place_id}
                    onClick={async () => {
                      const placeDetails = await getPlaceDetails(prediction);
                      handleSelectLocation(
                        [],
                        placeDetails.description,
                        placeDetails.lat ?? 0,
                        placeDetails.lon ?? 0
                      );
                    }}
                  >
                    {prediction.description}
                  </PlacePrediction>
                ))}
              </Box>
            </>
          )}
          {!isAreaSearchStarted && !isAreaSearchSelected && (
            <Divider>
              <Typography>Or select states</Typography>
            </Divider>
          )}
        </Box>
      }
    />
  );
}
